const DEFAULT_EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const BASE64_DATA_URL_PREFIX = /^data:[^,]+;base64,/i
const EXCEL_BASE64_PREFIX = 'UEsDB'

function getHeaderValue (headers, name) {
  if (!headers) return ''
  return headers[name] || headers[name.toLowerCase()] || headers[name.toUpperCase()] || ''
}

function normalizeContentType (contentType) {
  return (contentType || '').split(';')[0].trim()
}

function resolveContentType (response, blob) {
  const headerType = normalizeContentType(getHeaderValue(response && response.headers, 'content-type'))
  if (headerType && headerType !== 'text/plain') {
    return headerType
  }

  const blobType = normalizeContentType(blob && blob.type)
  if (blobType && blobType !== 'text/plain') {
    return blobType
  }

  return DEFAULT_EXCEL_CONTENT_TYPE
}

function normalizeBase64Payload (text) {
  return (text || '')
    .replace(/^\uFEFF/, '')
    .replace(BASE64_DATA_URL_PREFIX, '')
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/\s+/g, '')
}

function extractBase64ExcelPayload (text) {
  if (typeof text !== 'string') {
    return ''
  }

  const rawText = (text || '').trim()
  if (!rawText) {
    return ''
  }

  if (rawText.startsWith('{') || rawText.startsWith('[') || rawText.startsWith('"')) {
    try {
      const parsed = JSON.parse(rawText)
      if (typeof parsed === 'string') {
        return extractBase64ExcelPayload(parsed)
      }
      if (parsed && typeof parsed.data === 'string') {
        return extractBase64ExcelPayload(parsed.data)
      }
    } catch (error) {
    }
  }

  const normalized = normalizeBase64Payload(rawText)
  if (normalized.startsWith(EXCEL_BASE64_PREFIX)) {
    return normalized
  }

  const matched = normalized.match(/UEsDB[A-Za-z0-9+/=]+/)
  return matched ? matched[0] : ''
}

function decodeBase64ToBlob (base64Payload, contentType) {
  const binaryString = window.atob(base64Payload)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  return new Blob([bytes], { type: contentType || DEFAULT_EXCEL_CONTENT_TYPE })
}

export function resolveFilename (headers, fallbackName) {
  const disposition = getHeaderValue(headers, 'content-disposition')
  if (!disposition) {
    return fallbackName
  }

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1])
    } catch (error) {
      return utf8Match[1]
    }
  }

  const match = disposition.match(/filename="?([^";\n]+)"?/i)
  if (match && match[1]) {
    return match[1]
  }

  return fallbackName
}

export async function readBlobErrorMessage (blob) {
  if (!(blob instanceof Blob)) {
    return ''
  }

  try {
    const preview = (await blob.slice(0, 64).text()).trim()
    const contentType = normalizeContentType(blob.type)
    if (!contentType.includes('application/json') && !preview.startsWith('{')) {
      return ''
    }

    const text = await blob.text()
    const json = JSON.parse(text)
    return json.msg || text
  } catch (error) {
    return ''
  }
}

async function normalizeDownloadBlob (response, blob) {
  const preview = await blob.slice(0, 256).text()
  const previewPayload = extractBase64ExcelPayload(preview)
  if (!previewPayload) {
    return blob
  }

  const base64Payload = extractBase64ExcelPayload(await blob.text())
  if (!base64Payload) {
    return blob
  }

  return decodeBase64ToBlob(base64Payload, resolveContentType(response, blob))
}

export async function downloadBlobResponse (response, fallbackName) {
  const blob = response && response.data
  if (!(blob instanceof Blob)) {
    throw new Error('下载响应不是文件流')
  }

  const errorMessage = await readBlobErrorMessage(blob)
  if (errorMessage) {
    throw new Error(errorMessage)
  }

  const filename = resolveFilename(response.headers, fallbackName)
  const normalizedBlob = await normalizeDownloadBlob(response, blob)
  const url = window.URL.createObjectURL(normalizedBlob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export async function resolveDownloadError (error, fallbackMessage) {
  const blobMessage = await readBlobErrorMessage(error && error.response && error.response.data)
  return blobMessage || (error && error.message) || fallbackMessage || '下载失败'
}
